import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

const BLOCK_GAP_ENFORCER_KEY = new PluginKey('blockGapEnforcer');

/**
 * Ensures there is always an editable paragraph between adjacent handlebarsBlock
 * nodes, and before a leading handlebarsBlock. This allows users to place their
 * cursor between blocks to type content.
 *
 * Gap paragraphs are editor-only — they are stripped by convertToTemplate().
 * They are visually styled as thin placeholders via node decorations so they
 * look distinct from intentional empty lines.
 */
export const BlockGapEnforcer = Extension.create({
  name: 'blockGapEnforcer',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: BLOCK_GAP_ENFORCER_KEY,

        appendTransaction(transactions, _oldState, newState) {
          // Skip if this transaction was created by us
          for (const tr of transactions) {
            if (tr.getMeta(BLOCK_GAP_ENFORCER_KEY)) return null;
          }

          const { doc, schema } = newState;
          const insertions: number[] = [];

          function findGaps(parent: ProseMirrorNode, contentStart: number) {
            let prevWasBlock = false;
            let isFirst = true;

            parent.forEach((child, offset) => {
              const childPos = contentStart + offset;

              if (child.type.name === 'handlebarsBlock') {
                // Gap between consecutive blocks
                if (prevWasBlock) {
                  insertions.push(childPos);
                }
                // Leading gap: first child is a block
                if (isFirst) {
                  insertions.push(childPos);
                }
                prevWasBlock = true;
                // Recurse into nested block content
                findGaps(child, childPos + 1);
              } else {
                prevWasBlock = false;
              }

              isFirst = false;
            });

            // Trailing gap inside a handlebarsBlock parent (TrailingNode handles doc level)
            if (parent.type.name === 'handlebarsBlock' && prevWasBlock) {
              insertions.push(contentStart + parent.content.size);
            }
          }

          findGaps(doc, 0);

          if (insertions.length === 0) return null;

          const { tr } = newState;
          const paragraph = schema.nodes.paragraph.create();

          // Insert in reverse document order to avoid position shifts
          for (const pos of insertions.sort((a, b) => b - a)) {
            tr.insert(pos, paragraph);
          }

          tr.setMeta('addToHistory', false);
          tr.setMeta(BLOCK_GAP_ENFORCER_KEY, true);
          return tr;
        },

        props: {
          decorations(state) {
            const { doc } = state;
            const decorations: Decoration[] = [];

            function findGapParagraphs(parent: ProseMirrorNode, contentStart: number) {
              const children: { node: ProseMirrorNode; pos: number }[] = [];
              parent.forEach((child, offset) => {
                children.push({ node: child, pos: contentStart + offset });
              });

              for (let i = 0; i < children.length; i++) {
                const { node, pos } = children[i];

                if (node.type.name === 'handlebarsBlock') {
                  // Recurse into nested blocks
                  findGapParagraphs(node, pos + 1);
                } else if (
                  node.type.name === 'paragraph' &&
                  node.content.size === 0
                ) {
                  // Check if this empty paragraph is adjacent to a handlebarsBlock
                  const prev = i > 0 ? children[i - 1] : null;
                  const next = i < children.length - 1 ? children[i + 1] : null;
                  const prevIsBlock = prev?.node.type.name === 'handlebarsBlock';
                  const nextIsBlock = next?.node.type.name === 'handlebarsBlock';

                  if (prevIsBlock || nextIsBlock) {
                    decorations.push(
                      Decoration.node(pos, pos + node.nodeSize, {
                        class: 'block-gap-placeholder',
                      })
                    );
                  }
                }
              }
            }

            findGapParagraphs(doc, 0);

            return DecorationSet.create(doc, decorations);
          },
        },
      }),
    ];
  },
});
